import { useEffect, useState } from "react";
import { sanitizeHtml } from "../lib/sanitize";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail, withDlToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Mail, Phone, ShieldAlert, ShieldCheck, User, FileText, ReceiptText } from "lucide-react";
import PhoneField, { buildE164 } from "@/components/PhoneField";
import AlertSettingsCard from "@/components/AlertSettingsCard";
import { DEFAULT_PHONE_COUNTRY } from "@/lib/phoneCountries";

export default function Account() {
  const { t, i18n } = useTranslation();
  const { refreshUser } = useAuth();
  const [p, setP] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState("");

  // email change
  const [newEmail, setNewEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  // phone change
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [phoneLocal, setPhoneLocal] = useState("");
  const [phoneCode, setPhoneCode] = useState("");

  const load = () => api.get("/account/profile").then((r) => { setP(r.data); setName(r.data.name || ""); }).catch(() => {});
  const [invoices, setInvoices] = useState([]);
  const loadInvoices = () => api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); loadInvoices(); /* eslint-disable-next-line */ }, []);

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("account.actionImpossible"));
  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); if (okMsg) toast.success(r?.data?.message || okMsg); await load(); await refreshUser?.(); return r; }
    catch (e) { err(e); }
    finally { setBusy(""); }
  };

  if (!p) return <div className="p-8 text-slate-400" data-testid="account-loading">{t("account.loading")}</div>;

  const pending = p.pending_change;
  const emailPending = pending?.type === "email";
  const phonePendingOld = pending?.type === "phone" && pending?.stage === "verify_old";
  const phonePendingNew = pending?.type === "phone" && pending?.stage === "verify_new";

  const fmtInvDate = (iso) => { try { return new Date(iso).toLocaleDateString(i18n.language); } catch { return "—"; } };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 lg:px-10" data-testid="account-page">
      <header className="mb-8">
        <p className="overline text-[#C6A87C]">{t("account.overline")}</p>
        <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">{t("account.title")}</h1>
        <p className="mt-2 text-slate-500">{t("account.subtitle")}</p>
      </header>

      {!p.sms_configured && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="account-sms-note">
          {t("account.smsNote")}
        </div>
      )}

      {/* Profil */}
      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="account-profile-card">
        <h2 className="mb-4 flex items-center gap-2 font-display text-xl text-[#0F172A]"><User className="h-5 w-5 text-[#C6A87C]" /> {t("account.infoTitle")}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-slate-700">{t("account.nameLabel")}</Label>
            <Input data-testid="account-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" />
          </div>
          <div>
            <Label className="text-slate-700">{t("account.loginEmailLabel")}</Label>
            <Input data-testid="account-login-email" value={p.email} disabled className="mt-1.5 h-11 bg-slate-50" />
          </div>
        </div>
        <Button data-testid="account-save-name" disabled={busy === "name"} onClick={() => run("name", () => api.put("/account/profile", { name }), t("account.saved"))}
                className="mt-4 h-10 rounded-full bg-[#0F172A] px-6 text-white hover:bg-[#1e293b]">
          {busy === "name" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.save")}
        </Button>
      </section>

      {/* Adresse d'alerte */}
      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="account-alert-email-card">
        <h2 className="mb-1 flex items-center gap-2 font-display text-xl text-[#0F172A]"><Mail className="h-5 w-5 text-[#C6A87C]" /> {t("account.alertTitle")}</h2>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-slate-600">{t("account.activeAddress")} <b className="text-[#0F172A]" data-testid="account-alert-email">{p.alert_email}</b></span>
          {p.email_alerts_suspended
            ? <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700" data-testid="account-alert-suspended"><ShieldAlert className="h-3.5 w-3.5" /> {t("account.alertsSuspended")}</span>
            : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid="account-alert-active"><ShieldCheck className="h-3.5 w-3.5" /> {t("account.alertsActive")}</span>}
        </div>
        <p className="mb-4 text-sm text-slate-500">{t("account.alertDesc")}</p>

        {!p.email_alerts_suspended && !emailPending && (
          <Button variant="outline" data-testid="account-report-lost-btn" disabled={busy === "lost"}
                  onClick={() => run("lost", () => api.post("/account/email/report-lost"), t("account.alertsSuspendedOk"))}
                  className="h-10 rounded-full border-red-200 text-red-700 hover:bg-red-50">
            {busy === "lost" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.reportLost")}
          </Button>
        )}

        {(p.email_alerts_suspended || emailPending) && (
          <div className="rounded-xl border border-slate-100 p-4">
            {!emailPending ? (
              <div className="space-y-3">
                <Label className="text-slate-700">{t("account.newEmailLabel")}</Label>
                <Input type="email" data-testid="account-new-email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={t("account.newEmailPlaceholder")} className="h-11" />
                <Button data-testid="account-email-start-btn" disabled={busy === "estart" || !p.sms_configured || !newEmail}
                        onClick={() => run("estart", () => api.post("/account/email/change/start", { new_email: newEmail }), t("account.smsSentOk"))}
                        className="h-10 rounded-full bg-[#C6A87C] px-6 text-[#0F172A] hover:bg-[#b8986a]">
                  {busy === "estart" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.sendSms")}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600" data-testid="account-email-pending"><span dangerouslySetInnerHTML={sanitizeHtml(t("account.emailPending", { target: pending.target_masked }))} /></p>
                <Label className="text-slate-700">{t("account.codeLabel")}</Label>
                <Input data-testid="account-email-code" value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))} placeholder="123456" maxLength={6} className="h-11 w-40 tracking-[0.3em]" />
                <div className="flex gap-2">
                  <Button data-testid="account-email-verify-btn" disabled={busy === "everify" || emailCode.length !== 6}
                          onClick={() => run("everify", () => api.post("/account/email/change/verify", { code: emailCode }), t("account.newEmailValidated"))}
                          className="h-10 rounded-full bg-[#0F172A] px-6 text-white hover:bg-[#1e293b]">
                    {busy === "everify" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.validateCode")}
                  </Button>
                  <Button variant="ghost" data-testid="account-email-cancel-btn" onClick={() => run("ecancel", () => api.post("/account/email/change/cancel"), t("account.cancelledOk"))} className="h-10 rounded-full">{t("account.cancel")}</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Réglages des alertes e-mail */}
      <AlertSettingsCard />

      {/* Téléphone */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="account-phone-card">
        <h2 className="mb-1 flex items-center gap-2 font-display text-xl text-[#0F172A]"><Phone className="h-5 w-5 text-[#C6A87C]" /> {t("account.phoneTitle")}</h2>
        <p className="mb-4 text-sm text-slate-600">{t("account.phoneCurrent")} <b className="text-[#0F172A]" data-testid="account-phone">{p.phone || "—"}</b></p>
        <p className="mb-4 text-sm text-slate-500"><span dangerouslySetInnerHTML={sanitizeHtml(t("account.phoneDesc"))} /></p>

        {!(phonePendingOld || phonePendingNew) ? (
          <div className="space-y-3">
            <Label className="text-slate-700">{t("account.newPhoneLabel")}</Label>
            <PhoneField country={phoneCountry} local={phoneLocal} onCountryChange={setPhoneCountry} onLocalChange={setPhoneLocal} testid="account-newphone" />
            <Button data-testid="account-phone-start-btn" disabled={busy === "pstart" || !p.sms_configured || !phoneLocal || emailPending}
                    onClick={() => run("pstart", () => api.post("/account/phone/change/start", { new_phone: buildE164(phoneCountry, phoneLocal), new_phone_country: phoneCountry }), t("account.codeSentCurrent"))}
                    className="h-10 rounded-full bg-[#C6A87C] px-6 text-[#0F172A] hover:bg-[#b8986a]">
              {busy === "pstart" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.changePhone")}
            </Button>
            {emailPending && <p className="text-xs text-amber-700">{t("account.finishEmailFirst")}</p>}
          </div>
        ) : (
          <div className="rounded-xl border border-slate-100 p-4 space-y-3">
            <p className="text-sm text-slate-600" data-testid="account-phone-stage">
              {phonePendingOld ? t("account.phoneStage1") : t("account.phoneStage2", { target: pending.target_masked })}
            </p>
            <Label className="text-slate-700">{t("account.codeLabel")}</Label>
            <Input data-testid="account-phone-code" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))} placeholder="123456" maxLength={6} className="h-11 w-40 tracking-[0.3em]" />
            <div className="flex gap-2">
              {phonePendingOld ? (
                <Button data-testid="account-phone-verify-current-btn" disabled={busy === "pv1" || phoneCode.length !== 6}
                        onClick={() => run("pv1", () => api.post("/account/phone/change/verify-current", { code: phoneCode }), t("account.identityConfirmed")).then(() => setPhoneCode(""))}
                        className="h-10 rounded-full bg-[#0F172A] px-6 text-white hover:bg-[#1e293b]">
                  {busy === "pv1" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.validateStep1")}
                </Button>
              ) : (
                <Button data-testid="account-phone-verify-new-btn" disabled={busy === "pv2" || phoneCode.length !== 6}
                        onClick={() => run("pv2", () => api.post("/account/phone/change/verify-new", { code: phoneCode }), t("account.phoneUpdated")).then(() => setPhoneCode(""))}
                        className="h-10 rounded-full bg-[#0F172A] px-6 text-white hover:bg-[#1e293b]">
                  {busy === "pv2" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.validateStep2")}
                </Button>
              )}
              <Button variant="ghost" data-testid="account-phone-cancel-btn" onClick={() => run("pcancel", () => api.post("/account/phone/change/cancel"), t("account.cancelledOk"))} className="h-10 rounded-full">{t("account.cancel")}</Button>
            </div>
          </div>
        )}
      </section>

      {/* Mes factures */}
      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="account-invoices-card">
        <h2 className="mb-1 flex items-center gap-2 font-display text-xl text-[#0F172A]"><ReceiptText className="h-5 w-5 text-[#C6A87C]" /> {t("account.invoicesTitle")}</h2>
        <p className="mb-4 text-sm text-slate-500">{t("account.invoicesDesc")}</p>
        {invoices.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400" data-testid="account-no-invoices">{t("account.noInvoices")}</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 font-medium">{t("account.colNumber")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("account.colDate")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("account.colPlan")}</th>
                  <th className="px-3 py-2.5 font-medium text-right">{t("account.colTtc")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("account.colStatus")}</th>
                  <th className="px-3 py-2.5 font-medium text-right">{t("account.colPdf")}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100 last:border-0" data-testid={`account-invoice-${inv.number}`}>
                    <td className="px-3 py-2.5 font-medium text-[#0F172A]">{inv.number}</td>
                    <td className="px-3 py-2.5 text-slate-500">{fmtInvDate(inv.issue_date)}</td>
                    <td className="px-3 py-2.5 text-slate-500">{inv.type === "credit_note" ? t("account.creditNote") : inv.type === "corrective" ? t("account.corrective") : ""}{inv.plan_name}</td>
                    <td className="px-3 py-2.5 text-right font-medium text-[#0F172A]">{inv.type === "credit_note" ? "-" : ""}{Number(inv.amount_ttc).toFixed(2).replace(".", ",")} €</td>
                    <td className="px-3 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : inv.status === "refunded" ? "bg-red-100 text-red-700" : "bg-slate-200 text-slate-600"}`}>{inv.status === "paid" ? t("account.statusPaid") : inv.status === "refunded" ? t("account.statusRefunded") : inv.status === "cancelled" ? t("account.statusCancelled") : inv.status}</span></td>
                    <td className="px-3 py-2.5 text-right">
                      <a href={withDlToken(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`)} target="_blank" rel="noreferrer" data-testid={`account-invoice-pdf-${inv.number}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#C6A87C] bg-[#C6A87C]/10 px-3 py-1.5 text-xs font-semibold text-[#8a6d3b] transition hover:bg-[#C6A87C] hover:text-[#0F172A]"><FileText className="h-4 w-4" /> {t("account.downloadPdf", "Télécharger la facture PDF")}</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
